import { useGlobalSearchParams, useSegments } from "expo-router";
import { store } from "expo-router/build/global-state/router-store";
import { defaultRouteInfo } from "expo-router/build/global-state/routeInfo";
import { observeRouteSource } from "../analytics";
import type { Route, RouteParams } from "../route";
import { setRouteSource } from "../state";

/**
 * Reports the current expo-router route: sends `Screen Viewed` when the screen
 * or its tenant/site/camera changes, and gives every event the route's context.
 * Call once, in the root layout.
 *
 * Two paths feed the same route:
 *
 * - `track` reads expo-router's route info at the moment of the call
 *   (`focusedRoute`). A newly focused screen writes its route there while it
 *   renders, before its own render and effects, but expo-router tells
 *   subscribers (this layout included) only on the navigation container's
 *   `state` event, after those effects. Reading at `track` time is what gives
 *   an event a new screen tracks on mount the new route, with that route's
 *   `Screen Viewed` sent just before it.
 * - This hook reports during render, for screens that track nothing
 *   themselves. It subscribes through `useSegments` and
 *   `useGlobalSearchParams`, so the layout re-renders on each route change,
 *   but reports through the same `focusedRoute` as `track`, so both paths skip
 *   the same things and the core sends `Screen Viewed` once.
 *
 * Skipped: expo-router's placeholder route info. On Android the initial URL
 * resolves asynchronously (`expo-router/build/link/linking.js`), so until it
 * does `getRouteInfo()` returns `defaultRouteInfo` (`segments: []`), which
 * would otherwise be reported as `/` and become the first screen's
 * `previous_screen`. Malformed route info is skipped the same way.
 *
 * Segments are the route's file path, so they are a template already
 * (`["tenants", "[tenantId]", "(tabs)", "cameras"]`). expo-router drops a
 * trailing `index` (`app/index.tsx` gives `[]`, so `/`), and the core drops
 * route groups. Params hold path and query params together, which is where the
 * scope params come from (site may be either).
 */
export function useRouteAnalytics(): void {
  setRouteSource(focusedRoute);
  // Called to subscribe (re-render on route changes); the route itself is read
  // from `focusedRoute`, so the placeholder and malformed shapes are skipped.
  useSegments();
  useGlobalSearchParams();
  observeRouteSource();
}

/**
 * expo-router's route info (`store.getRouteInfo()`, what `useSegments` and
 * `useGlobalSearchParams` read) as it stands now. `BaseRoute` in
 * `expo-router/build/useScreens.js` calls `store.setFocusedState` while a
 * focused screen renders; `global-state/router-store.js` replaces it on each
 * container `state` event. These are internals, not public API: pinned to the
 * app's copy in `apps/a3s-app/metro.config.js`, and re-checked on upgrade.
 * Before the router has route info it returns its shared default, which is
 * no route. Because they are internals, the shape is checked rather than
 * trusted: anything but string segments and plain-object params is no route,
 * so an upgrade that changes it falls back to the last reported route.
 */
function focusedRoute(): Route | undefined {
  const info: unknown = store.getRouteInfo();
  if (info === defaultRouteInfo || !isRecord(info)) {
    return undefined;
  }
  const { segments, params } = info;
  if (!isStringArray(segments) || !isRouteParams(params)) {
    return undefined;
  }
  return { segments, params };
}

function isRecord(value: unknown): value is Readonly<Record<string, unknown>> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isStringArray(value: unknown): value is readonly string[] {
  return Array.isArray(value) && value.every((item) => typeof item === "string");
}

function isRouteParams(value: unknown): value is RouteParams {
  return (
    isRecord(value) &&
    Object.values(value).every((param) => param === undefined || typeof param === "string" || isStringArray(param))
  );
}
